import React, { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import Layout from '../layouts/application';
import { ThemeProvider } from '@mui/material/styles';
import { themeAttuned } from '../../constants/theme';
import { ConfirmDialogProvider } from '../../components/modals/confirm_dialog';
import { Paper } from '@mui/material';
import SurveyCollectionsNav from '../../components/survey_collections/nav';
import { trackCzEvent } from '../../helpers/track_cz_event';

const Container = (props) => {
  const { org_id, base_url, show_tableau, tableau_url, jwt, include_school_name, school_name, links, permission_props } = props;

  const vizRef = useRef(null);

  useEffect(() => {
    if (!show_tableau) return;

    const viz = vizRef.current;
    if (!viz) return;

    const onFirstInteractive = async (event) => {
      trackCzEvent('SparkView:Dashboard', `User launched Spark dashboard via View button.`, 1);
    };

    const onParameterChanged = async (event) => {
      const parameter = await event.detail.getParameterAsync();
      if (parameter.parameterImpl._parameterInfo.name === 'View video parameter') {
        trackCzEvent('SparkClick:Video', `User clicked Spark tutorial video ${parameter.parameterImpl._parameterInfo.currentValue.value}`, 1);
      }

      if (parameter.parameterImpl._globalFieldName === '[Parameters].[Parameter 1]') {
        const parameterName = parameter.parameterImpl._parameterInfo.currentValue.formattedValue;
        trackCzEvent('SparkFilter:Disaggregation', `User applied disaggregation filter ${parameterName}`, 1);
      }
    };

    const onTabSwitched = async (event) => {
      const tabName = event.detail._newSheetName;
      if (['Org Theme Summary', 'School Theme Summary', 'Org Themes with Quotes', 'School Themes with Quotes', 'All Quotes'].includes(tabName)) {
        trackCzEvent('SparkView:Tab', `User viewed Spark tab ${tabName}`, 1);
      } else if (['Download Org Wide Slides', 'Download School Wide Slides'].includes(tabName)) {
        trackCzEvent('SparkExport:Slides', 'User exported Spark slides/PDF.', 1);
      } else if (['Information'].includes(tabName)) {
        trackCzEvent('SparkClick:Resource', `User clicked Spark resource link`, 1);
      } else {
        trackCzEvent('SparkView:OpenEnded', `User viewed open-ended responses sub-tabs ${tabName}`, 1);
      }
    };

    viz.addEventListener('firstinteractive', onFirstInteractive);
    viz.addEventListener('parameterchanged', onParameterChanged);
    viz.addEventListener('tabswitched', onTabSwitched);

    return () => {
      viz.removeEventListener('firstinteractive', onFirstInteractive);
      viz.removeEventListener('parameterchanged', onParameterChanged);
      viz.removeEventListener('tabswitched', onTabSwitched);
    };
  }, [show_tableau, org_id, tableau_url, base_url]);

  return (
    <>
      <SurveyCollectionsNav links={links} permissionProps={permission_props} activeTab="data_analysis" backUrl={links.data_analysis} />
      <Paper className="p-3 mt-4">
        {show_tableau && (
          <tableau-viz ref={vizRef} id="tableauViz" style={{ height: 1000 }} src={tableau_url} token={jwt}>
            <viz-parameter name="orgid" value={org_id}></viz-parameter>
            {include_school_name && <viz-parameter name="schoolname" value={school_name}></viz-parameter>}
          </tableau-viz>
        )}
      </Paper>
    </>
  );
};

Container.propTypes = {
  org_id: PropTypes.number,
  base_url: PropTypes.string,
  links: PropTypes.object,
};

export default (props, railsContext) => {
  return () => (
    <Layout railsContext={railsContext}>
      <ThemeProvider theme={themeAttuned}>
        <ConfirmDialogProvider>
          <Container {...props} />
        </ConfirmDialogProvider>
      </ThemeProvider>
    </Layout>
  );
};
